import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { SuspendedAccountScreen } from "@/components/SuspendedAccountScreen";
import { PendingActivationScreen } from "@/components/PendingActivationScreen";
import { AccountSetupScreen } from "@/components/AccountSetupScreen";


export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, loading, isRecoverySession } = useAuth();
  const { currentTenant, userMemberships, loading: tenantLoading, allMembershipsSuspended, hasOnlyPendingMemberships, isOrphanNeedingOnboarding, isSuperAdmin } = useTenant();
  const location = useLocation();

  // Check if user requires password reset
  const { data: userData, isLoading: checkingUser } = useQuery({
    queryKey: ['user-data', session?.user?.id],
    queryFn: async () => {
      if (!session?.user) return null;
      
      const { data, error } = await supabase
        .from('app_users')
        .select('require_password_reset')
        .eq('id', session.user.id)
        .single();
      
      if (error) {
        console.error('Error checking user data:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!session?.user,
    staleTime: 600000, // 10 minutes - reduce protected route auth checks
    gcTime: 1800000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Get current user's role based on current tenant context
  const getCurrentUserRole = () => {
    if (!currentTenant || !userMemberships.length) return null;
    
    const currentMembership = userMemberships.find(m => m.tenant.id === currentTenant.id);
    return currentMembership?.role || null;
  };

  if (loading || checkingUser || tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!session) {
    const fromPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/auth" replace state={{ from: fromPath }} />;
  }

  // Account not yet activated → show pending screen (unless on recovery/setup flows)
  const allowedWhilePending = ['/change-password', '/setup-password'];

  // Keep the platform-only route out of normal tenant navigation. Pending
  // users will still be gated at /dashboard, without retaining /superadmin.
  if (!isSuperAdmin && location.pathname === '/superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Super admins have no memberships and no tenant by design. Skip the
  // orphan/pending/suspended gates entirely for them so they can reach
  // /superadmin without being sent through AccountSetupScreen.
  if (!isSuperAdmin) {
    // Orphan (no memberships, no activation request) → let them complete onboarding
    if (isOrphanNeedingOnboarding && !allowedWhilePending.includes(location.pathname)) {
      return <AccountSetupScreen />;
    }

    if (hasOnlyPendingMemberships && !allowedWhilePending.includes(location.pathname)) {
      return <PendingActivationScreen />;
    }

    // All memberships suspended → block app entry
    if (allMembershipsSuspended) {
      return <SuspendedAccountScreen />;
    }
  }

  // CRITICAL SECURITY: Recovery sessions can ONLY access /change-password
  // This prevents attackers with email access from gaining full account access
  if (isRecoverySession && location.pathname !== '/change-password') {
    console.log('🔒 Recovery session detected, redirecting to change-password');
    return <Navigate to="/change-password" replace />;
  }

  // Check if email is verified (but allow through if they need to set password)
  if (session.user && !session.user.email_confirmed_at && !userData?.require_password_reset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md mx-auto text-center p-6">
          <h1 className="text-2xl font-semibold text-foreground mb-4">
            Check your email
          </h1>
          <p className="text-muted-foreground mb-6">
            We've sent a confirmation link to <strong>{session.user.email}</strong>. 
            Please check your email and click the link to verify your account.
          </p>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
            className="text-primary hover:underline"
          >
            Sign out and try with a different email
          </button>
        </div>
      </div>
    );
  }

  // If user requires password reset and not already on setup-password page
  if (userData?.require_password_reset && location.pathname !== '/setup-password') {
    return <Navigate to="/setup-password" replace />;
  }

  // Role-based redirects and access control
  const currentRole = getCurrentUserRole();
  const currentPath = location.pathname;

  // Super admins bypass role-based redirects: they have no memberships and
  // land on /superadmin from root.
  if (isSuperAdmin) {
    if (currentPath === '/') {
      return <Navigate to="/superadmin" replace />;
    }
    return <>{children}</>;
  }

  // Redirect from root based on role
  if (currentPath === '/') {
    if (currentRole === 'driver') {
      return <Navigate to="/driver" replace />;
    } else if (['admin', 'dispatcher', 'operations', 'viewer'].includes(currentRole)) {
      return <Navigate to="/dashboard" replace />;
    } else if (['outlet_employee', 'outlet'].includes(currentRole)) {
      return <Navigate to="/outlet-portal" replace />;
    } else if (currentRole === 'factory_employee') {
      return <Navigate to="/factory-portal" replace />;
    }
  }

  // Prevent drivers from accessing admin/operations pages
  if (currentRole === 'driver') {
    const restrictedPaths = ['/dashboard', '/tasks-list', '/drivers-management', '/reports', '/map', '/track', '/orders', '/operations', '/admin'];
    if (restrictedPaths.some(path => currentPath.startsWith(path))) {
      return <Navigate to="/driver" replace />;
    }
  }

  // Prevent outlet users from accessing admin/operations pages
  if (['outlet_employee', 'outlet'].includes(currentRole)) {
    // Shared /auth sign-ins land on the dashboard. Keep outlet users' other
    // admin/operations restrictions in place.
    const restrictedPaths = ['/tasks-list', '/drivers-management', '/reports', '/map', '/track', '/orders', '/operations', '/admin'];
    if (restrictedPaths.some(path => currentPath.startsWith(path))) {
      return <Navigate to="/outlet-portal" replace />;
    }
  }

  // Prevent factory employees from accessing admin/operations pages
  if (currentRole === 'factory_employee') {
    // Shared /auth sign-ins land on the dashboard. Keep factory users' other
    // admin/operations restrictions in place.
    const restrictedPaths = ['/tasks-list', '/drivers-management', '/reports', '/map', '/track', '/orders', '/operations', '/admin', '/outlet-portal'];
    if (restrictedPaths.some(path => currentPath.startsWith(path))) {
      return <Navigate to="/factory-portal" replace />;
    }
  }

  return <>{children}</>;
}
